// Onboarding on a single screen (docs/project.md §4; Onboarding board).
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { isLanguageCode } from '@solungo/core/lang';
import { api } from '@/api/endpoints';
import { setUser, refreshDictionaries } from '@/state/session';
import { browserLanguage, pickUiLocale } from '@/i18n';
import { storage } from '@/lib/storage';
import { LanguagePicker, LanguageRow } from '@/components/LanguagePicker';
import { ErrorText, Group, Hint, PrimaryButton } from '@/components/ui';
import { AuthFrame } from '@/components/AuthFrame';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/onboarding')({
  component: Onboarding,
});

function Onboarding() {
  const t = useTranslations('onboarding');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  const pendingTarget = storage.getPendingTarget();
  const pendingLocale = storage.getPendingLocale();
  const [name, setName] = useState(user.name ?? '');
  const [nativeLang, setNativeLang] = useState<string>(
    (user.nativeLang && isLanguageCode(user.nativeLang) ? user.nativeLang : null) ?? (pendingLocale && isLanguageCode(pendingLocale) ? pendingLocale : browserLanguage()),
  );
  const [targetLang, setTargetLang] = useState<string | null>(pendingTarget && isLanguageCode(pendingTarget) ? pendingTarget : null);
  const [picker, setPicker] = useState<'native' | 'target' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!targetLang || !name.trim()) return;
    if (targetLang === nativeLang) {
      setError(t('sameLanguage'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const locale = pickUiLocale(nativeLang, pendingLocale, navigator.language);
      const res = await api.me.onboarding({ name: name.trim(), nativeLang, targetLang, locale });
      setUser(res.user);
      track('onboarding_completed', { name: name.trim(), target_lang: targetLang, native_lang: nativeLang });
      storage.setPendingTarget(null);
      storage.setPendingLocale(null);
      storage.setLastDictionary(res.dictionary.id);
      await refreshDictionaries();
      void navigate({ to: '/d/$dictionaryId', params: { dictionaryId: res.dictionary.id }, replace: true });
    } catch (e) {
      reportError(e);
      setError(tc('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame brand={false} title={t('title')} subtitle={t('subtitle')}>
      <form
        className="flex flex-col gap-5 desktop:gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="field-label">{t('name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus autoComplete="name" placeholder={t('namePlaceholder')} data-testid="name-input" className="field" />
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="field-label">{t('languages')}</span>
          <Group className="rounded-field desktop:rounded-[9px] desktop:border desktop:border-border">
            <LanguageRow label={t('nativeLang')} value={nativeLang} onClick={() => setPicker('native')} className="desktop:h-10 desktop:pl-3 desktop:pr-2.5 desktop:text-[14px]" />
            <LanguageRow label={t('targetLang')} value={targetLang} onClick={() => setPicker('target')} className="desktop:h-10 desktop:pl-3 desktop:pr-2.5 desktop:text-[14px]" />
          </Group>
          <Hint className="desktop:px-0 desktop:text-[12px] desktop:leading-[17px]">{t('hint')}</Hint>
        </div>
        <PrimaryButton busy={busy} disabled={!targetLang || !name.trim()} data-testid="start">
          {t('start')}
        </PrimaryButton>
        <ErrorText>{error}</ErrorText>
      </form>
      {picker && (
        <LanguagePicker
          title={picker === 'native' ? t('nativeLang') : t('targetLang')}
          value={picker === 'native' ? nativeLang : targetLang}
          exclude={picker === 'target' ? [nativeLang] : targetLang ? [targetLang] : []}
          codes={undefined}
          onSelect={(code) => {
            if (picker === 'native') setNativeLang(code);
            else setTargetLang(code);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </AuthFrame>
  );
}

